"use client";

import { notifications } from "@mantine/notifications";
import { ProductActions } from "Components/Admin/AdminProduct/AdminProduct/ProductActions";
import { ProductForm } from "Components/Admin/AdminProduct/AdminProduct/ProductForm";
import ProductReviewTable from "Components/Admin/AdminProduct/AdminProduct/ProductReviewTable";
import { ProductTable } from "Components/Admin/AdminProduct/AdminProduct/ProductTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { ProductAdminRequestDTO } from "Interface/Admin/Products/admin-product.interface";
import { useEffect, useState } from "react";
import {
  useCreateAdminProductMutation,
  useDeleteAdminProductMutation,
  useDeleteAdminProductsBulkMutation,
  useDeleteAdminProductMainImageMutation,
  useDeleteAdminProductOtherImageMutation,
  useGetAllAdminProductsQuery,
  useLazyGetAdminProductByIdQuery,
  useUpdateAdminProductMutation,
  useUploadAdminProductMainImageMutation,
  useUploadAdminProductOtherImagesMutation,
} from "Services/Admin/ProductAdminApiSlice";
import { useGetAllItemGroupsQuery } from "Services/Admin/ItemGroupApi";
import { ProductDetailDTO } from "Types/ProductDetailDTO";
import { ProductMetaDTO } from "Types/ProductMetaDTO";

const PLANT = process.env.REACT_APP_PLANT ?? "";

const AdminProduct = () => {
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReviews, setShowReviews] = useState(false);

  const [pageSize, setPageSize] = useState(100);
  const [activePage, setActivePage] = useState(1);
  const {
    data: productPacker,
    isLoading,
    isFetching,
    refetch,
  } = useGetAllAdminProductsQuery({
    category: "",
    subCategory: "",
    brand: "",
    activePage,
    pageSize,
  });

  const [createProduct] = useCreateAdminProductMutation();
  const [updateProduct] = useUpdateAdminProductMutation();
  const [deleteProduct] = useDeleteAdminProductMutation();
  const [deleteProductsBulk] = useDeleteAdminProductsBulkMutation();

  const [uploadMainImage] = useUploadAdminProductMainImageMutation();
  const [uploadAdditionalImages] = useUploadAdminProductOtherImagesMutation();
  const [deleteMainImage] = useDeleteAdminProductMainImageMutation();
  const [deleteOtherImage] = useDeleteAdminProductOtherImageMutation();

  const handleDeleteMainImage = async () => {
    const productId = editingProduct?.productWrapper?.product?.id;
    if (!productId) return;

    try {
      const res = await deleteMainImage(productId).unwrap();
      notifications.show({
        message: res.message,
        color: "teal",
      });
      // Refresh to reflect deletion
      refetch();
      if (editingProduct?.productWrapper?.product?.id) {
        const updated = await getProductDetailById({
          id: editingProduct.productWrapper.product.id,
        }).unwrap();
        setEditingProduct(updated);
      }
    } catch (error: any) {
      console.error("Failed to delete main image", error);
      notifications.show({
        message:
          error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleDeleteAdditionalImage = async (index: number) => {
    const productId = editingProduct?.productWrapper?.product?.id;
    if (!productId) return;

    try {
      // Backend expects 1-based line numbers for image slots
      const res = await deleteOtherImage({ id: productId, lnNo: index + 1 }).unwrap();
      notifications.show({
        message: res.message,
        color: "teal",
      });
      refetch();
      if (editingProduct?.productWrapper?.product?.id) {
        const updated = await getProductDetailById({
          id: editingProduct.productWrapper.product.id,
        }).unwrap();
        setEditingProduct(updated);
      }
    } catch (error: any) {
      console.error("Failed to delete additional image", error);
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  // const [editingProduct, setEditingProduct] = useState<ProductMetaDTO | null>(
  //   null
  // );
  const [editingProduct, setEditingProduct] = useState<ProductDetailDTO | null>(
    null,
  );
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductMetaDTO[]>(
    [],
  );
  const [filterOption, setFilterOption] = useState("All");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    let filtered = productPacker ? [...productPacker.products] : [];

    if (filterOption === "Active") {
      filtered = filtered.filter(
        (product) => product.product.isActive.toString() === "Y",
      );
    } else if (filterOption === "Inactive") {
      filtered = filtered.filter(
        (product) => product.product.isActive.toString() !== "Y",
      );
    }

    if (searchText) {
      filtered = filtered.filter(
        (product) =>
          product.product.itemDesc
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          product.product.item.toLowerCase().includes(searchText.toLowerCase()),
      );
    }

    setFilteredProducts(filtered);
  }, [productPacker, searchText]);

  //   useEffect(() => {
  //   if (selectedProductId) {
  //     setShowReviewModal(true);
  //   }
  // }, [selectedProductId]);

  const { data: itemGroups = [] } = useGetAllItemGroupsQuery(PLANT);

  const [getProductDetailById] = useLazyGetAdminProductByIdQuery();

  const handleViewProduct = async (id: number) => {
    if (typeof id !== "number") {
      console.error("Invalid product ID:", id);
      return;
    }

    try {
      const productDetail = await getProductDetailById({ id }).unwrap();

      if (productDetail && productDetail.productWrapper?.product?.id) {
        setEditingProduct(productDetail);
        setViewMode(true);
        setShowForm(true);
      } else {
        throw new Error("Invalid product data received");
      }
    } catch (error: any) {
      console.error("Failed to get product details:", error);
      notifications.show({
        message: (error as any)?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleEditProduct = async (id: number) => {
    if (typeof id !== "number") {
      console.error("Invalid product ID:", id);
      return;
    }
    try {
      const productDetail = await getProductDetailById({ id }).unwrap();
      if (productDetail && productDetail.productWrapper?.product?.id) {
        setEditingProduct(productDetail);
        setViewMode(false);
        setShowForm(true);
      } else {
        throw new Error("Invalid product data received");
      }
    } catch (error: any) {
      console.error("Failed to get product details:", error);
      notifications.show({
        message: (error as any)?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleSelectProductReview = async (id: number) => {
    setSelectedProductId(id);
    setShowReviewModal(true);
  };

  const handleSubmit = async (
    data: ProductAdminRequestDTO,
    mainImageFile: File | null,
    additionalImageFiles: (File | null)[],
    addImgsLineNo: number[],
  ) => {
    try {
      const additionalFilesWithIndex = additionalImageFiles
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => file !== null);

      const additionalFileArray = additionalFilesWithIndex.map(
        ({ file }) => file as File,
      );
      // Backend expects lnNo to be 1-based
      const lineNumbers = additionalFilesWithIndex.map(({ index }) => index + 1);

      if (editingProduct && editingProduct.productWrapper?.product?.id) {
        const response = await updateProduct({
          id: editingProduct.productWrapper.product.id,
          data,
        }).unwrap();
        const updatedProductId = editingProduct.productWrapper.product.id;

        if (mainImageFile) {
          const mainImgResponse = await uploadMainImage({
            id: updatedProductId,
            image: mainImageFile,
          }).unwrap();
        }

        if (additionalFileArray.length > 0) {
          const addImgResponse = await uploadAdditionalImages({
            id: updatedProductId,
            images: additionalFileArray,
            lnNo: lineNumbers,
          }).unwrap();
        }

        notifications.show({
          message: response.message,
          color: "teal",
        });
      } else {
        const response = await createProduct(data).unwrap();
        const newProductId = response.results;

        if (mainImageFile) {
          const mainImgResponse = await uploadMainImage({
            id: newProductId,
            image: mainImageFile,
          }).unwrap();
        }

        if (additionalFileArray.length > 0) {
          const addImgResponse = await uploadAdditionalImages({
            id: newProductId,
            images: additionalFileArray,
            lnNo: lineNumbers,
          }).unwrap();
        }

        notifications.show({
          message: response.message,
          color: "teal",
        });
      }

      refetch();
      setShowForm(false);
      setEditingProduct(null);
    } catch (error: any) {
      console.error("Full Error Details:", error);
      notifications.show({
        message: (error as any)?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleDeleteProduct = async (id: number) => {
    try {
      const res = await deleteProduct(id).unwrap();
      notifications.show({
        message: res.message,
        color: "teal",
      });
      refetch();
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
      console.error("Failed to delete product:", error);
    }
  };

  const handleBulkDeleteProducts = async (ids: number[]) => {
    if (!ids || ids.length === 0) return;

    try {
      const res = await deleteProductsBulk(ids).unwrap();
      notifications.show({
        message: res.message,
        color: "teal",
      });
      refetch();
      setSelectedProductIds([]);
      setSelectedProductId(null);
    } catch (error: any) {
      console.error("Failed to bulk delete products:", error);
      notifications.show({
        message: error.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const productMappingFromProductDetailDTOToRequest = (
    editingProduct: ProductDetailDTO | null,
  ): ProductAdminRequestDTO | null => {
    if (
      !editingProduct ||
      !editingProduct.productWrapper ||
      !editingProduct.productWrapper.product
    ) {
      return null;
    }

    const product = editingProduct.productWrapper.product;

    const mappedData: ProductAdminRequestDTO = {
      productCode: product.item || "",
      productName: product.itemDesc || "",
      title: product.title || "",
      remark1: product.remark1 || "", 
      productDescription: editingProduct.detailDesc || [],
      category: editingProduct.categoryWrapper?.id || "",
      subCategory: editingProduct.subCategoryWrapper?.id || "",
      brand: editingProduct.brandWrapper?.id || "",
      // subClassId: editingProduct.subClassWrapper?.subClassCode || "",
      subClassId: product?.subClassId || "",
      author: product?.author || "",
      isbn: product?.isbn || "",
      isbnPrice: product?.isbnPrice ?? null,
      formatType:
        product?.formatType === "HARDCOVER" ||
        product?.formatType === "PAPERBACK"
          ? product.formatType
          : undefined,

      itemCondition:
        product?.itemCondition === "NEW" || product?.itemCondition === "USED"
          ? product.itemCondition
          : undefined,

      productCondition: product?.productCondition || undefined,
      preownedCondition: product?.preownedCondition || undefined,

      language: product?.language || "",
      academic: product?.academic || "",
      merchandise: product?.merchandise || "",
      purchaseUom: product.purchaseUom || "",
      cost: product.cost || 0,
      salesUom: product.salesUom || "",
      mrp: product.mrp || 0,
      sellingPrice: product.sellingPrice || 0,
      minimumSellingPrice: product.minimumSellingPrice || 0,
      inventoryUom: product.inventoryUom || "",
      minimumStkQty: product.minimumStkQty || 0,
      maximumStkQty: product.maximumStkQty || 0,
      isNewArrival: product.isNewArrival || 0,
      isTopSelling: product.isTopSelling || 0,
      isActive: product.isActive?.toString() || "Y",
      model: product.model || "",
      baseUom: product.baseUom || "",
      remarkTwo: product.remarkTwo || "",
      productGarmentType: editingProduct.productGarmentTypeDTO || {
        id: 0,
        size: "",
        fabric: "",
        collar: "",
        color: "",
        sleeve: "",
        occasion: "",
        pattern: "",
      },
      gst: product.gst || 0,
      cgst: product.cgst || 0,
      sgst: product.sgst || 0,
      cess: product.cess || 0,
      hsCode: product.hsCode || "",
      minimumInvQty: product.minimumInvQty || 0,
      labelPara1: product.labelPara1 || "",
      labelPara2: product.labelPara2 || "",
      labelPara3: product.labelPara3 || "",
      labelPara4: product.labelPara4 || "",
      labelPara5: product.labelPara5 || "",
      labelPara6: product.labelPara6 || "",
      labelPara7: product.labelPara7 || "",
      labelPara8: product.labelPara8 || "",
      parameter1: product.parameter1 || "",
      parameter2: product.parameter2 || "",
      parameter3: product.parameter3 || "",
      parameter4: product.parameter4 || "",
      parameter5: product.parameter5 || "",
      parameter6: product.parameter6 || "",
      parameter7: product.parameter7 || "",
      parameter8: product.parameter8 || "",
      detailDescription: product.detailDescription || "",
      itemGroupId: product.itemGroupId
        ? (Array.isArray(itemGroups)
            ? itemGroups.find(g => g.id === Number(product.itemGroupId))?.itemGroupName
            : undefined)
        : undefined,
    };

    return mappedData;
  };
  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Products"
            onAdd={() => setShowForm(true)}
            addLabel="Add Product"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <ProductActions
              onDelete={() => handleBulkDeleteProducts(selectedProductIds)}
              onFilterChange={setFilterOption}
              onSearchChange={setSearchText}
              isDeleteDisabled={selectedProductIds.length === 0}
              selectedCount={selectedProductIds.length}
            />

            <ProductTable
              products={filteredProducts}
              onSelectProduct={setSelectedProductId}
              onDeleteProduct={handleDeleteProduct}
              onViewProduct={handleViewProduct}
              onEditProduct={handleEditProduct}
              onSelectProductReview={handleSelectProductReview}
              selectedProduct={selectedProductId}
              isLoading={isLoading || isFetching}
              onPageChange={setActivePage}
              onItemsPerPageChange={setPageSize}
              currentPage={activePage}
              itemsPerPage={pageSize}
              totalItems={productPacker?.totalProducts || 0}
              selectedProducts={selectedProductIds}
              onSelectedProductsChange={setSelectedProductIds}
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingProduct(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Product"
                : editingProduct
                  ? "Edit Product"
                  : "Add Product"
            }
          />
          <ProductForm
            initialValues={
              productMappingFromProductDetailDTOToRequest(editingProduct) ||
              ({
                brand: "",
                category: "",
                cost: 0,
                inventoryUom: "pcs",
                isNewArrival: 0,
                isTopSelling: 0,
                maximumStkQty: 0,
                minimumSellingPrice: 0,
                minimumStkQty: 0,
                mrp: 0,
                productCode: "",
                productDescription: [],
                productName: "",
                purchaseUom: "pcs",
                salesUom: "pcs",
                sellingPrice: 0,
                subCategory: "",
                isActive: "Y",
                model: "",
                baseUom: "pcs",
                remarkTwo: "",
                productgarmenttype: {
                  id: 0,
                  size: "",
                  fabric: "",
                  collar: "",
                  color: "",
                  sleeve: "",
                  occasion: "",
                  pattern: "",
                },
                gst: 0,
                cgst: 0,
                sgst: 0,
                cess: 0,
                hsCode: "",
                minimumInvQty: 0,
                labelPara1: "",
                labelPara2: "",
                labelPara3: "",
                labelPara4: "",
                labelPara5: "",
                labelPara6: "",
                labelPara7: "",
                labelPara8: "",
                parameter1: "",
                parameter2: "",
                parameter3: "",
                parameter4: "",
                parameter5: "",
                parameter6: "",
                parameter7: "",
                parameter8: "",
                detailDescription: "",
                itemGroupId: undefined,
              } as unknown as ProductAdminRequestDTO)
            }
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingProduct(null);
            }}
            existingMainImage={
              editingProduct?.productWrapper.imagePath || undefined
            }
            existingAdditionalImages={(() => {
              const sparse: (string | undefined)[] = Array(5).fill(undefined);
              const paths = editingProduct?.imagePaths ?? [];
              const lnNos = editingProduct?.imagesLnNo;
              if (lnNos && lnNos.length === paths.length) {
                lnNos.forEach((lnNo, i) => {
                  if (lnNo >= 1 && lnNo <= 5) sparse[lnNo - 1] = paths[i];
                });
              } else {
                paths.forEach((path, i) => { if (i < 5) sparse[i] = path; });
              }
              return sparse as string[];
            })()}
            onDeleteMainImage={handleDeleteMainImage}
            onDeleteAdditionalImage={handleDeleteAdditionalImage}
            mode={viewMode ? "view" : editingProduct ? "edit" : "add"}
          />

          {/* Product Reviews & Ratings — collapsible */}
          {editingProduct?.productWrapper?.product?.id ? (
            <div className="mt-8 border rounded-lg overflow-hidden bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setShowReviews((prev) => !prev)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-gray-800">
                    Product Reviews &amp; Ratings
                  </span>
                 
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    showReviews ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showReviews && (
                <div className="px-5 pb-5 pt-2 border-t bg-white">
                  <ProductReviewTable
                    productId={editingProduct.productWrapper.product.id}
                  />
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
      {showReviewModal && selectedProductId && (
        <div className="fixed inset-0 z-50 bg-black/40">
          {/* MODAL */}
          <div
            className="
        fixed
        top-1/2 left-1/2
        -translate-x-1/2 -translate-y-1/2
        bg-white
        w-[95vw] max-w-6xl
        rounded-lg shadow-lg
        flex flex-col
        max-h-[90vh]
      "
          >
            {/* HEADER */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">
                Product Reviews & Ratings
              </h2>
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedProductId(null);
                }}
                aria-label="Close"
                className="w-15 h-15
        flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-100transition text-2xl font-medium"
              >
                ×
              </button>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ProductReviewTable productId={selectedProductId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProduct;
